import { Badge } from "@design";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { type FlowStepConfig, type FlowTriggerCreate, listAgentsOptions } from "@/api";
import { Text } from "@/components/Text";
import { FlowDraftCanvas } from "@/features/build/workflows/FlowDraftCanvas";
import type { WorkflowDraftBody } from "./useWorkflowDraft";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

/** A trigger in words, as the Triggers tab says it (in UTC unless the draft names a timezone). */
function triggerWhen(t: TFunction<["workflows", "buildChat"]>, trigger: FlowTriggerCreate): string {
  const s = trigger.schedule;
  if (trigger.triggerType === "webhook") return t("workflows:triggers.webhookNote");
  if (trigger.triggerType !== "scheduled" || !s) return t("workflows:triggers.manualNote");
  const time = s.runAtTime ?? "09:00";
  const days = (s.daysOfWeek ?? []).map((d) => t(`workflows:triggers.weekdays.${WEEKDAYS[d] ?? "mon"}`)).join(", ");
  const tz = s.timezone && s.timezone !== "UTC" ? s.timezone : null;
  if (s.scheduleType === "weekly") {
    return tz
      ? t("buildChat:workflow.when.weekly", { days, time, tz })
      : t("workflows:triggers.preview.weekly", { days, time, timezone: "UTC" });
  }
  if (s.scheduleType === "custom") {
    const count = s.intervalDays ?? 1;
    return tz
      ? t("buildChat:workflow.when.custom", { count, time, tz })
      : t("workflows:triggers.preview.custom", { count, time, timezone: "UTC" });
  }
  return tz
    ? t("buildChat:workflow.when.daily", { time, tz })
    : t("workflows:triggers.preview.daily", { time, timezone: "UTC" });
}

/** A workflow draft: its purpose, the steps (canvas + list with agents and review gates) and its triggers. */
export function WorkflowDraftSummary({ body, isUpdate }: Readonly<{ body: WorkflowDraftBody; isUpdate: boolean }>) {
  const { t } = useTranslation(["workflows", "buildChat"]);
  const agents = useQuery({ ...listAgentsOptions(), select: (r) => r.data });
  const flow = body.flow ?? {};
  const steps: FlowStepConfig[] = flow.steps ?? [];
  const triggers = body.triggers ?? [];
  return (
    <div className="space-y-3">
      {(flow.purpose || flow.description) && <Text variant="body-sm">{flow.purpose ?? flow.description}</Text>}
      {steps.length > 0 && <FlowDraftCanvas steps={steps} connectors={flow.connectors ?? []} />}
      {steps.length > 0 && (
        <ol aria-label={t("buildChat:workflow.stepsLabel")} className="space-y-1">
          {steps.map((step, order) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: steps have no id until created; their order is the identity.
              key={order} // NOSONAR
              className="flex flex-wrap items-center gap-2"
            >
              <Text variant="body-sm">
                {t("buildChat:workflow.step", {
                  n: order + 1,
                  agent: agents.data?.find((a) => a.uuid === step.agentUuid)?.name ?? t("buildChat:workflow.noAgent"),
                  text: step.instructions ?? "",
                })}
              </Text>
              {step.requiresReview && <Badge variant="warning">{t("buildChat:workflow.needsReview")}</Badge>}
            </li>
          ))}
        </ol>
      )}
      {triggers.map((trigger) => (
        <Text key={trigger.name} variant="body-sm" tone="secondary">
          {t("buildChat:workflow.trigger", { name: trigger.name, when: triggerWhen(t, trigger) })}
        </Text>
      ))}
      {isUpdate && triggers.length > 0 && (
        <Text variant="caption" tone="tertiary">
          {t("buildChat:workflow.triggersOnTab")}
        </Text>
      )}
    </div>
  );
}
